import axios, { AxiosInstance, AxiosResponse, ResponseType } from 'axios';

import { parseISO } from 'date-fns';
import config from '../config';
import { Language } from '../stores/LanguageStore';
import { Address } from '../stores/models/Address';
import { CustomerInvoicesSettings, InvoiceSettings } from '../stores/models/CustomerInvoicesSettings';
import DebtNotice from '../stores/models/DebtNotice';
import { DownloadableFile } from '../stores/models/DownloadableFile';
import { IInvoiceDetailsRequest } from '../stores/models/IInvoiceDetailsRequest';
import { Invoice } from '../stores/models/Invoice';
import { InvoiceDetailsResponse } from '../stores/models/InvoiceDetailsResponse';
import { InvoiceFile } from '../stores/models/InvoiceFile';
import { InvoiceFilter } from '../stores/models/InvoiceFilter';
import { InvoiceLineUsageRow } from '../stores/models/InvoiceLineUsageRow';
import { InvoiceSettlement } from '../stores/models/InvoiceSettlement';
import { Mandate } from '../stores/models/Mandate';
import { PaymentDetails } from '../stores/models/PaymentDetails';
import { PaymentHistory } from '../stores/models/PaymentHistory';
import { PosPayment } from '../stores/models/PosPayment';
import { PosPaymentRequest } from '../stores/models/PosPaymentRequest';
import { Prepayment } from '../stores/models/Prepayment';
import { PublicLinkInvoiceBalance } from '../stores/models/PublicLinkInvoiceBalance';
import { Transaction } from '../stores/models/Transaction';
import { TransactionFilter } from '../stores/models/TransactionFilter';
import { UnsettledReceivables } from '../stores/models/UnsettledReceivables';
import { SsoService } from '../stores/SsoService';

import { MobileNumberValidation } from '../stores/models/MobileNumberValidation';
import { EmailValidation } from '../stores/models/EmailValidation';
import { InvoiceSettingsChangeLog } from '../stores/models/InvoiceSettingsChangeLog';
import { EInvoiceSupplier } from '../stores/models/EInvoiceSupplier';
import { IbanValidation } from '../stores/models/IbanValidation';
import { InvoiceSettingsSaveResponse } from '../stores/models/InvoiceSettingsSaveResponse';
import { CurrentBalances } from '../stores/models/CurrentBalances';
import { InvoiceFileTypesRequest } from '../stores/models/InvoiceFileTypesRequest';
import { InvoiceFileRequest } from '../stores/models/InvoiceFileRequest';
import { InvoiceDeliveryLog } from '../stores/models/InvoiceDeliveryLog';
import { parseToISODate } from '../util/DateUtil';
import ApiError from './ApiError';

export type FileExtension = 'PDF' | 'CSV' | 'XML' | 'XLSX';
export type FileType = 'INVOICE_PDF' | 'INVOICE_CSV' | 'INVOICE_XML' | 'USAGES_CSV' | 'XLSX';
export type Source = 'TBCIS' | 'DAX' | 'BILL';
export type InvoiceFileType = 'INVOICE_XML' | 'INVOICE_PDF' | 'INVOICE_CSV' | 'USAGES_CSV' | 'EXTRAS_PDF';

export default class ApiService {
  public static API_BASE_URL_DEFAULT = '/diil-billing-frontend/api';
  private static SHORT_TIMEOUT = 30000;
  private static LONG_TIMEOUT = 50000;

  private apiBaseUrl = config.apiUrl || ApiService.API_BASE_URL_DEFAULT;

  private httpClient;

  constructor(
    private ssoService: SsoService,
    axiosInstance?: AxiosInstance
  ) {
    this.httpClient = axiosInstance || this.getAxiosInstance(ApiService.SHORT_TIMEOUT);
  }

  public getPreviousInvoices(customerId: number, filter?: InvoiceFilter): Promise<Invoice[]> {
    return this.handleError(
      this.httpClient
        .get(`/customers/${customerId}/previous-invoices`, { params: { ...filter } })
        .then((response) => response.data)
        .then((rawInvoices) => this.mapToInvoices(rawInvoices))
    );
  }

  public downloadInvoiceFile(invoiceFileRequest: InvoiceFileRequest): Promise<DownloadableFile> {
    return this.downloadFile(ApiService.invoiceFileUrl(invoiceFileRequest), ApiService.getFileExtensionFromFileName(invoiceFileRequest.fileType));
  }

  public fullInvoiceFileUrl(invoiceFileRequest: InvoiceFileRequest): string {
    return `${ApiService.API_BASE_URL_DEFAULT}/${ApiService.invoiceFileUrl(invoiceFileRequest)}`;
  }

  public getUnsettledReceivables(customerId: number): Promise<UnsettledReceivables> {
    return this.handleError(
      this.httpClient
        .get(`/customers/${customerId}/unsettled-receivables`)
        .then((response) => response.data)
        .then((rawUnsettledReceivables) => {
          rawUnsettledReceivables.unpaidInvoices = this.mapToInvoices(rawUnsettledReceivables.unpaidInvoices);
          rawUnsettledReceivables.prepayments = this.mapToPrepayments(rawUnsettledReceivables.prepayments);
          return Object.assign(new UnsettledReceivables(), rawUnsettledReceivables);
        })
    );
  }

  public getPaymentHistory(customerId: number, filter?: TransactionFilter): Promise<PaymentHistory> {
    return this.handleError(
      this.httpClient
        .get(`/customers/${customerId}/transactions`, { params: { ...filter } })
        .then((response) => response.data)
        .then((rawPaymentHistory) => this.mapToPaymentHistory(rawPaymentHistory))
    );
  }

  public getPaymentDetails(customerId: number, bindingId: number): Promise<PaymentDetails> {
    return this.getRequest(`/customers/${customerId}/payment-details/${bindingId}`).then((rawPaymentDetails) =>
      ApiService.mapToPaymentDetails(rawPaymentDetails)
    );
  }

  public getMandate(customerId: number): Promise<Mandate> {
    return this.handleError(this.httpClient.get(`/customers/${customerId}/mandates`).then((response) => response.data));
  }

  public getInvoiceDetails(invoice: IInvoiceDetailsRequest): Promise<InvoiceDetailsResponse> {
    return this.handleError(
      this.httpClient
        .get(`/customers/${invoice.customerId}/invoices/${invoice.invoiceNumber}/details`, {
          params: {
            source: invoice.source,
            subscriber: invoice.subscriber
          }
        })
        .then((response) => response.data)
        .then((rawJson) => ApiService.mapToInvoiceDetails(rawJson))
    );
  }

  public getInvoiceLineUsage(customerId: number, invoiceNumber: string, invoiceLineId: string, source: string): Promise<InvoiceLineUsageRow[]> {
    if (invoiceLineId == null || source == null) {
      return Promise.reject('invoice line id must be given');
    }
    return this.handleError(
      this.httpClient
        .get(`/customers/${customerId}/invoices/${invoiceNumber}/usages/${invoiceLineId}`, { params: { source } })
        .then((response) => response.data)
        .then((rawJson) => this.mapToInvoiceLineUsages(rawJson))
    );
  }

  public getMasterRefNumber(customerId: number): Promise<string> {
    return this.getRequest(`/customers/${customerId}/master-reference-number`);
  }

  public getInvoiceSettings(customerId: number): Promise<CustomerInvoicesSettings> {
    return this.getRequest(`/customers/${customerId}/invoice-settings`);
  }

  public getInvoiceSettingsChangeLog(customerId: number, referenceNumber: string, source: string): Promise<InvoiceSettingsChangeLog> {
    return this.getRequest(`/customers/${customerId}/invoice-settings/${referenceNumber}/change-log?source=${source}`).then((rawChangeLog) => {
      return rawChangeLog;
    });
  }

  public getEInvoiceSuppliers(): Promise<EInvoiceSupplier[]> {
    return this.getRequest('/einvoicesuppliers');
  }

  public getDebtNotice(customerId: number): Promise<DebtNotice> {
    return this.handleError(
      this.httpClient
        .get(`/customers/${customerId}/debt-notice`)
        .then((response) => response.data)
        .then((debtNoticeResource) => ApiService.mapToDebtNotice(debtNoticeResource))
    );
  }

  public createPayment(customerId: number, paymentRequest: PosPaymentRequest): Promise<PosPayment> {
    return this.postRequest(`/customers/${customerId}/create-pos-payment`, paymentRequest);
  }

  public getInvoiceFileTypes(invoiceRequest: InvoiceFileTypesRequest): Promise<InvoiceFileType[]> {
    return this.getRequest(`customer/${invoiceRequest.customerId}/invoices/${invoiceRequest.invoiceNumber}/file-types?source=${invoiceRequest.source}`);
  }

  private downloadFile(url: string, fileExtension: FileExtension): Promise<DownloadableFile> {
    return this.getAxiosInstance(ApiService.LONG_TIMEOUT, 'blob')
      .get(url, { headers: { Accept: ApiService.getAcceptHeader(fileExtension) } })
      .then((response: AxiosResponse) => {
        const fileName = response.headers['content-disposition'].replace('attachment; filename=', '');
        return new DownloadableFile(response.data, fileName);
      });
  }

  public downloadPaymentHistoryFile(customerId: number, filter: TransactionFilter): Promise<DownloadableFile> {
    return this.downloadFile(`/customers/${customerId}/transactions/xlsx?fromDate=${filter.fromDate}&toDate=${filter.toDate}`, 'XLSX');
  }

  public syncNonPeriodicInvoicesToBill(customerId: number): Promise<number> {
    return this.postRequest(`/customers/${customerId}/sync/non-periodic-invoices`, 'sync-non-periodic-invoices');
  }

  private static getAcceptHeader(fileExtension: FileExtension): string {
    if (fileExtension === 'PDF') {
      return 'application/pdf';
    }
    if (fileExtension === 'XML') {
      return 'application/xml';
    }
    if (fileExtension === 'CSV') {
      return 'text/csv;charset=UTF-8';
    }
    if (fileExtension === 'XLSX') {
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    return '*/*';
  }

  public downloadInvoice(
    customerId: number,
    invoiceNumber: string,
    source: string,
    language: Language,
    fileType: FileType,
    fileExtension: FileExtension
  ): Promise<InvoiceFile> {
    return this.downloadFile(`/customers/${customerId}/invoices/${invoiceNumber}?file-type=${fileType}&source=${source}&language=${language}`, fileExtension);
  }

  public downloadInvoiceUsages(
    customerId: number,
    invoiceNumber: string,
    source: string,
    fileType: FileType,
    fileExtension: FileExtension
  ): Promise<InvoiceFile> {
    return this.downloadFile(`/customers/${customerId}/invoices/${invoiceNumber}/usages?source=${source}`, fileExtension);
  }

  public saveInvoiceSettings(customerId: number, referenceNumber: string, invoiceSetting: InvoiceSettings): InvoiceSettingsSaveResponse {
    return this.putRequest(`/customers/${customerId}/invoice-settings/${referenceNumber}`, invoiceSetting) as any;
  }

  public getTranslations(lang: string): any {
    return this.httpClient.get(`/translations/${lang}`).then((res) => res.data);
  }

  public handleError(promise: Promise<any>): Promise<any> {
    return promise.catch((res) => {
      if (res.response.status === 403) {
        this.ssoService.doLogout();
      } else {
        console.error('Failure', res);
        throw new ApiError(`Received error from backend ${res}`);
      }
    });
  }

  public getAxiosInstance(timeout: number, responseType?: ResponseType): AxiosInstance {
    return axios.create({
      baseURL: this.apiBaseUrl,
      timeout,
      responseType
    });
  }

  public transformSettlements(settlementsData: any): InvoiceSettlement[] {
    const settlements: InvoiceSettlement[] = [];
    settlementsData.forEach((settlement) => {
      settlement.date = parseToISODate(settlement.date);
      settlements.push(Object.assign(new InvoiceSettlement(), settlement));
    });
    return settlements;
  }

  public validateAccount(account: string, bankCode?: string): Promise<IbanValidation> {
    let queryParams = '';
    if (bankCode != null) {
      queryParams = `?bankCode=${bankCode}`;
    }
    return this.handleError(this.httpClient.get(`/accounts/${account}/validate${queryParams}`).then((response) => response.data));
  }

  public validateEmail(email: string): Promise<EmailValidation> {
    return this.handleError(this.httpClient.get(`/emails/${email}/validate`).then((response) => response.data));
  }

  public validateMobileNumber(mobileNumber: string): Promise<MobileNumberValidation> {
    return this.handleError(this.httpClient.get(`/mobiles/${mobileNumber}/validate`).then((response) => response.data));
  }

  public getAddressSuggestions(term: string): Promise<Address[]> {
    return this.handleError(this.httpClient.get('/addresses/suggest', { params: { term: term }, timeout: 30000 }).then((response) => response.data));
  }

  public getBalance(mac: string, invoiceId: string, refNumber: string, source: string, bankCode: string): Promise<PublicLinkInvoiceBalance> {
    return this.getRequest(`/publiclinks/balance?mac=${mac}&invoiceId=${invoiceId}&refNumber=${refNumber}&source=${source}&bankCode=${bankCode}`);
  }

  public getCurrentBalances(customerId: number): Promise<CurrentBalances> {
    return this.getRequest(`/customers/${customerId}/current-balances`);
  }

  public getInvoiceDeliveryLog(customerId: number, invoiceNumber: string): Promise<InvoiceDeliveryLog[]> {
    return this.getRequest(`/customers/${customerId}/invoices/${invoiceNumber}/delivery-log`).then((invoiceDeliveryLog) =>
      this.mapToInvoiceDeliveryLogs(invoiceDeliveryLog)
    );
  }

  private mapToInvoiceDeliveryLogs(rawInvoiceDeliveryLog: any[]): InvoiceDeliveryLog[] {
    return rawInvoiceDeliveryLog.map((deliveryLog) => {
      return Object.assign(new InvoiceDeliveryLog(), deliveryLog);
    });
  }

  private mapToInvoices(rawInvoices: any[]): Invoice[] {
    return rawInvoices.map((invoice) => {
      invoice.issuedDate = parseToISODate(invoice.issuedDate);
      invoice.dueDate = parseToISODate(invoice.dueDate);
      invoice.settlements = this.transformSettlements(invoice.settlements);
      return Object.assign(new Invoice(), invoice);
    });
  }

  private static mapToPaymentDetails(rawPaymentDetails: any): PaymentDetails {
    rawPaymentDetails.date = parseToISODate(rawPaymentDetails.date);
    return Object.assign(new PaymentDetails(), rawPaymentDetails);
  }

  private mapToPrepayments(rawPrepayments: any[]): Prepayment[] {
    return rawPrepayments.map((prepayment) => {
      prepayment.date = parseToISODate(prepayment.date);
      return Object.assign(new Prepayment(), prepayment);
    });
  }

  private mapToPaymentHistory(rawPaymentHistory: any): PaymentHistory {
    rawPaymentHistory.transactions = rawPaymentHistory.transactions.map((rawTransaction) => {
      rawTransaction.date = parseToISODate(rawTransaction.date);
      rawTransaction.invoiceDueDate = parseToISODate(rawTransaction.invoiceDueDate);
      return Object.assign(new Transaction(), rawTransaction);
    });
    return Object.assign(new PaymentHistory(), rawPaymentHistory);
  }

  private static mapToDebtNotice(rawDebtNoticeResource: any): DebtNotice {
    const rawDebtNotice = rawDebtNoticeResource.debtNotice;
    rawDebtNotice.dueDate = parseToISODate(rawDebtNotice.dueDate);
    return Object.assign(new DebtNotice(), rawDebtNotice);
  }

  private static mapToInvoiceDetails(rawInvoiceDetails: any): InvoiceDetailsResponse {
    return Object.assign(new InvoiceDetailsResponse(), rawInvoiceDetails);
  }

  private mapToInvoiceLineUsages(rawInvoiceLineUsageRows: any[]): InvoiceLineUsageRow[] {
    rawInvoiceLineUsageRows.forEach((usageRow) => (usageRow.timestamp = parseISO(usageRow.timestamp)));
    return rawInvoiceLineUsageRows.map((row) => Object.assign(new InvoiceLineUsageRow(), row));
  }

  private getRequest(uri: string) {
    return this.handleError(this.httpClient.get(uri).then((response) => response.data));
  }

  private postRequest(uri: string, data?: any) {
    return this.handleError(this.httpClient.post(uri, data)).then((response) => response.data);
  }

  private putRequest(uri: string, data?: any) {
    return this.handleError(this.httpClient.put(uri, data).then((response) => response.data));
  }

  private static invoiceFileUrl(invoiceFileRequest: InvoiceFileRequest): string {
    return `customer/${invoiceFileRequest.customerId}/invoices/${invoiceFileRequest.invoiceNumber}/file-type/${invoiceFileRequest.fileType}?source=${invoiceFileRequest.source}`;
  }

  private static getFileExtensionFromFileName(fileName: InvoiceFileType): FileExtension {
    const fileType: string[] = fileName.split('_');

    return fileType[1] as FileExtension;
  }
}
